import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Lock, Unlock, Upload, Download } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { utcDate, fmtTime } from '../approvals/shared';

const API = `${window.__BACKEND_URL__}/api`;

const csvEscape = (v) => {
  if (v == null) return '';
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};

export const LockBar = ({ token, period, readinessPct, onChanged, onOpenPush }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [lockResult, setLockResult] = useState(null);
  // Unlock pay period (Oct 2026): admins can re-open a locked period to fix a missed review item.
  const [lockState, setLockState] = useState(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [unlockResult, setUnlockResult] = useState(null);
  const loadLockState = async () => {
    if (!period.start || !period.end) return;
    try {
      const { data } = await axios.get(`${API}/time/pay-period-lock-state`, { ...auth, params: { start_date: period.start, end_date: period.end } });
      setLockState(data);
    } catch { setLockState(null); }
  };
  const doUnlock = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/time/unlock-pay-period`, { start_date: period.start, end_date: period.end, reason: unlockReason.trim() }, auth);
      setUnlockResult(data);
      setUnlockOpen(false); setUnlockReason('');
      toast({ title: 'Pay period unlocked', description: `${data.unlocked} entr${data.unlocked === 1 ? 'y' : 'ies'} back to payroll-approved · ${data.period_locks_removed} period lock${data.period_locks_removed === 1 ? '' : 's'} removed${data.paid_kept ? ` · ${data.paid_kept} already-paid entr${data.paid_kept === 1 ? 'y' : 'ies'} untouched` : ''}.` });
      setLockResult(null);
      await loadLockState();
      onChanged?.();
    } catch (e) {
      toast({ title: 'Could not unlock', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally { setBusy(false); }
  };
  const batchRef = `PR-${period.end || new Date().toISOString().slice(0, 10)}`;

  useEffect(() => {
    setLockResult(null);
    setUnlockResult(null);
    loadLockState();
  }, [period.start, period.end]);

  const doLock = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/time/lock-pay-period`, {
        start_date: period.start,
        end_date: period.end,
        batch_reference: batchRef,
        override_reason: fullyReady ? null : overrideReason.trim() || null,
      }, auth);
      setLockResult(data);
      setUnlockResult(null);
      await loadLockState();
      setOverrideOpen(false);
      setOverrideReason('');
      toast({ title: 'Pay period locked', description: `${data.locked} locked, ${data.skipped} skipped` });
      onChanged?.();
    } catch (e) {
      toast({ title: 'Could not lock', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const fetchLockedEntries = async () => {
    const { data } = await axios.get(`${API}/payroll/period-entries`, {
      ...auth,
      params: { status: 'locked,paid', start_date: period.start, end_date: period.end },
    });
    return data.entries || [];
  };

  const downloadCsv = async () => {
    try {
      const entries = await fetchLockedEntries();
      if (entries.length === 0) {
        toast({ title: 'Nothing to download', description: 'No locked/paid entries in this period.', variant: 'destructive' });
        return;
      }
      const header = ['Worker', 'ID', 'Date', 'Clock In', 'Clock Out', 'Reg Hrs', 'OT Hrs', 'Pay Code', 'Job'];
      const rows = entries.map((e) => [
        e.user_name, e.id, utcDate(e.clock_in).toISOString().slice(0, 10),
        fmtTime(e.clock_in), e.clock_out ? fmtTime(e.clock_out) : '',
        (e.regular_hours ?? 0).toFixed(2), (e.overtime_hours ?? 0).toFixed(2),
        e.pay_code || '', e.job_number || e.project_number || e.area_label || '',
      ].map(csvEscape).join(','));
      const blob = new Blob([[header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll_${period.start}_to_${period.end}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Download failed', variant: 'destructive' });
    }
  };

  const fullyReady = readinessPct >= 100;

  return (
    <>
      <div className="sticky bottom-0 z-40 -mx-4 md:-mx-6 px-4 md:px-6 py-3"
        style={{ background: 'rgba(11,19,33,0.95)', backdropFilter: 'blur(12px)', borderTop: '1px solid #2D3748' }}
        data-testid="payroll-lock-bar">
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => (fullyReady ? doLock() : setOverrideOpen(true))}
            disabled={busy || !period.start || !period.end}
            title={fullyReady ? 'Lock this pay period' : `Only ${readinessPct}% ready — locking now will skip unready entries`}
            className={fullyReady ? 'bg-[#00C896] hover:bg-emerald-600 text-white font-semibold' : 'bg-amber-600 hover:bg-amber-700 text-white font-semibold'}
            data-testid="lock-pay-period-btn">
            <Lock className="w-4 h-4 mr-1.5" />
            {fullyReady ? 'Lock Pay Period' : `Lock Pay Period (${readinessPct}% ready)`}
          </Button>
          {!fullyReady && (
            <span className="text-[11px]" style={{ color: '#FBBF24' }} data-testid="lock-warning-hint">
              ⚠ Not 100% ready — locking requires an override reason
            </span>
          )}
          {lockState?.is_locked && (
            <Button variant="outline" onClick={() => setUnlockOpen(true)} disabled={busy}
              title="Re-open this pay period so time entries can be corrected"
              className="border-amber-600/60 text-amber-200 hover:bg-amber-500/10"
              data-testid="unlock-pay-period-btn">
              <Unlock className="w-4 h-4 mr-1.5" /> Unlock Pay Period
              <span className="ml-1.5 text-[11px] opacity-80" data-testid="unlock-pay-period-count">
                ({lockState.locked_entries} locked{lockState.period_locks?.length ? ' · period lock on' : ''})
              </span>
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenPush?.()}
              title="Build a preview, compare with QuickBooks, back up a CSV, then confirm & push"
              className="border-[#2D3748] text-white"
              data-testid="open-process-push-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Push to QuickBooks →
            </Button>
            <Button variant="outline" onClick={downloadCsv} disabled={busy}
              className="border-[#2D3748] text-white"
              data-testid="download-csv-btn">
              <Download className="w-4 h-4 mr-1.5" /> Download CSV
            </Button>
          </div>
        </div>
        {unlockResult && (
          <p className="text-xs mt-2" style={{ color: '#FBBF24' }} data-testid="unlock-result">
            🔓 Unlocked {unlockResult.unlocked} entr{unlockResult.unlocked === 1 ? 'y' : 'ies'} — now editable again. Re-lock when the fix is done.
          </p>
        )}
        {lockResult && (
          <p className="text-xs mt-2" style={{ color: '#00C896' }} data-testid="lock-result-line">
            🔒 Locked {lockResult.locked} entr{lockResult.locked === 1 ? 'y' : 'ies'}
            {lockResult.skipped > 0 && <span style={{ color: '#FBBF24' }}> · {lockResult.skipped} skipped (not payroll-approved)</span>}
          </p>
        )}
      </div>

      {overrideOpen && (
        <Dialog open onOpenChange={(o) => !o && setOverrideOpen(false)}>
          <DialogContent className="max-w-md" data-testid="lock-override-modal">
            <DialogHeader>
              <DialogTitle>Lock at {readinessPct}% readiness?</DialogTitle>
              <DialogDescription>
                Entries that aren't payroll-approved will be SKIPPED and stay unlocked.
                Type a reason to confirm the early lock.
              </DialogDescription>
            </DialogHeader>
            <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="e.g. Payroll deadline today — remaining entries roll to next period"
              data-testid="lock-override-reason" />
            <p className={`text-[11px] text-right ${overrideReason.trim().length >= 10 ? 'text-slate-500' : 'text-amber-400'}`}>
              {overrideReason.trim().length}/10 characters minimum
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOverrideOpen(false)} data-testid="lock-override-cancel">Cancel</Button>
              <Button onClick={doLock} disabled={busy || overrideReason.trim().length < 10}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="lock-override-confirm">
                {busy ? 'Locking…' : '🔒 Lock Anyway'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {unlockOpen && (
        <Dialog open onOpenChange={(o) => !o && setUnlockOpen(false)}>
          <DialogContent className="max-w-md" data-testid="unlock-modal">
            <DialogHeader>
              <DialogTitle>Unlock {period.start} → {period.end}?</DialogTitle>
              <DialogDescription>
                {lockState?.locked_entries || 0} locked entr{lockState?.locked_entries === 1 ? 'y' : 'ies'} go back to payroll-approved and time edits are accepted again.
                {lockState?.paid_entries ? ` ${lockState.paid_entries} entr${lockState.paid_entries === 1 ? 'y' : 'ies'} already pushed/paid stay as they are.` : ''}
                {' '}The reason is kept in the audit log.
              </DialogDescription>
            </DialogHeader>
            <Input value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)}
              placeholder="e.g. Missed Guoming Li's Tuesday hours in review"
              data-testid="unlock-reason" />
            <p className={`text-[11px] text-right ${unlockReason.trim().length >= 5 ? 'text-slate-500' : 'text-amber-400'}`}>
              {unlockReason.trim().length}/5 characters minimum
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUnlockOpen(false)} data-testid="unlock-cancel">Cancel</Button>
              <Button onClick={doUnlock} disabled={busy || unlockReason.trim().length < 5}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="unlock-confirm">
                {busy ? 'Unlocking…' : 'Unlock Pay Period'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default LockBar;
